import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert
} from '@mui/material';
import { Warning, Save, Logout } from '@mui/icons-material';

interface SessionTimeoutWarningProps {
  open: boolean;
  timeLeft: number; // in seconds
  onExtendSession: () => void;
  onSaveAndLogout: () => void;
  onLogout: () => void;
}

const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  open,
  timeLeft,
  onExtendSession,
  onSaveAndLogout,
  onLogout
}) => {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (open && timeLeft > 0) {
      const interval = setInterval(() => {
        setProgress((timeLeft / 300) * 100); // Assuming 5 minutes total warning time
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [open, timeLeft]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      disableBackdropClick
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="warning" />
          <Typography variant="h6">Session Timeout Warning</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your session will expire in {formatTime(timeLeft)}. Please choose an action to continue.
        </Alert>
        
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Time remaining:
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            color={timeLeft < 60 ? 'error' : 'warning'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Typography variant="body1" gutterBottom>
          You have unsaved changes in your loan application form. What would you like to do?
        </Typography>

        <Box component="ul" sx={{ mt: 2, pl: 2 }}>
          <li>
            <Typography variant="body2">
              <strong>Extend Session:</strong> Continue working on your application
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Save & Logout:</strong> Save your progress and log out safely
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <strong>Logout:</strong> Log out without saving (changes will be lost)
            </Typography>
          </li>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onLogout}
          color="error"
          startIcon={<Logout />}
          variant="outlined"
        >
          Logout
        </Button>
        <Button
          onClick={onSaveAndLogout}
          color="primary"
          startIcon={<Save />}
          variant="outlined"
        >
          Save & Logout
        </Button>
        <Button
          onClick={onExtendSession}
          color="primary"
          variant="contained"
          autoFocus
        >
          Extend Session
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarning;
